import { useReducer, useCallback, useEffect, useRef, useState } from "react";
import { Loader2, X } from "@digital-twins/design-system/icons";
import { DisplayFontsLoader } from "@/components/shared/DisplayFontsLoader";
import { Button } from "@/components/ui/button";
import type { PopulationFilter } from "@/types/routes/test-results";
import type { StudyDesign } from "@digital-twins/shared-types";
import { selectDeepLinkedAudience, isRelaunchingStudyAudienceStatus, isSelectableStudyAudienceStatus } from "@/lib/audience-selectability";
import { RelaunchingAudienceDialog } from "@/components/audiences/RelaunchingAudienceDialog";
import {
  creationReducer,
  initialCreationState,
  pickedAudienceOf,
  reviewedPoolOf,
} from "./state/creationState";
import { STUDY_CREATION_TYPE } from "./types";
import { useRecordRecentOpen } from "@/hooks/useRecordRecentOpen";
import { useStudyCreationData } from "./hooks/useStudyCreationData";
import { useStudyCreationDraftResume } from "./hooks/useStudyCreationDraftResume";
import { useStudyBriefHandoff, useStudyCreationHandoff, studyHandoffEditorFilter, handoffGoalSeed } from "./studyBriefHandoff";
import { useCreationStepNavigation } from "./hooks/useCreationStepNavigation";
import { useStudyActions } from "./hooks/useStudyActions";
import { useStudyCreationBeforeUnload } from "./hooks/useStudyCreationBeforeUnload";
import { useReviewedFilteredCount } from "./hooks/useReviewedFilteredCount";
import { CreationFlowErrorBoundary } from "./components/CreationFlowErrorBoundary";
import {
  AudienceStepWrapper,
  ChatStepWrapper,
  ReviewStepWrapper,
} from "./components";
import { GoalSelectionStep } from "./components/goals/GoalSelectionStep";
import { StudySetupModal } from "./components/goals/setup/StudySetupModal";
import { GoalChip } from "./components/goals/GoalChip";
import { getGoalById } from "./components/goals/goalsConfig";
import type { GoalId, GoalIntakePayload } from "./components/goals/types";
import { AnalyticsEvents, track } from "@/lib/services/analytics";
import { ROOM_LANGUAGES } from "@/lib/constants/languages";
import { useProfile } from "@/lib/hooks";
import { useTrialStudyLimit } from "@/lib/hooks/use-trial-limits";
import { useRouter, useSearchParams } from "@/routing/navigation";
import { ROUTES } from "@/routes/routes";
import { isThreadEmbedLocation } from "@/features/studies/embed/studyEmbedBridge";
import { useEmbedMode } from "@/features/studies/embed/useEmbedMode";
import {
  usePublishStudyEmbedDraft,
  useStudyEmbedBridge,
} from "@/features/studies/embed/useStudyEmbedBridge";

const RESUME_DRAFT_STORAGE_KEY = "dtfg_resume_draft_pending";
import { LockedFreeGate } from "@/components/billing/LockedFreeGate";
import { UpgradeModal } from "@/components/billing/UpgradeModal";
import { isTrialLike } from "@/lib/billing/tier";
import { DuplicateActionModal } from "./components/DuplicateActionModal";
import type { DuplicateAction } from "./components/DuplicateActionModal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { isStudyTypeClosed, useClosedStudyTypes } from "@/hooks/useClosedStudyTypes";
import type { AudienceFilterLabel, SelectedSegment } from "@digital-twins/shared-types";

function StudyCreationFlowInner() {
  const { t } = useTranslation("app");
  const router = useRouter();
  const searchParams = useSearchParams();
  const isEmbed = useEmbedMode();
  const [draftPresentation, setDraftPresentation] = useState<"full" | "quick">("full");
  const isCompactEmbed = isEmbed && draftPresentation === "quick";
  const resumeDraftId = searchParams.get("draftId");
  useRecordRecentOpen("draft", resumeDraftId);
  const fromDuplicate = searchParams.get("from") === "duplicate";
  const handoff = useStudyCreationHandoff();
  const deepLinkPopulationId =
    searchParams.get("audience") || searchParams.get("populationId") || handoff?.audienceToken || null;
  // Study-language hand-off from the Home Lab composer's language swapper
  // (`useHomeLabSubmit.ts`'s `/tests/new` URL append). Validated against
  // `ROOM_LANGUAGES` where it is dispatched, below: an unsupported code is
  // ignored rather than stored, never coerced to 'en'.
  const deepLinkLanguage = searchParams.get("language");

  // Study-type hand-off from the Home Lab composer (`useHomeLabSubmit.ts`'s
  // `buildStudyCreationHandoff`). Home Lab has a study-type picker the live
  // homepage never had, and its value used to die on the homepage: the href
  // carried only the audience and the brief, so `goalId` stayed null all the way
  // down and the backend ran its generic opening chain instead of the picked
  // one. `getGoalById` VALIDATES as it resolves, so an unknown id (or a Home
  // Lab action like `paste_questionnaire`, which is not a goal) reads as no
  // goal at all rather than as a goal the flow cannot look up.
  const deepLinkGoal = getGoalById(searchParams.get("goal"));
  const closedStudyTypes = useClosedStudyTypes();
  const isDeepLinkGoalClosed = isStudyTypeClosed(
    closedStudyTypes,
    deepLinkGoal?.id,
  );
  const handedGoalId = handoff?.goalId ?? null;
  const handoffGoalId = deepLinkGoal ? null : handoffGoalSeed(handedGoalId, closedStudyTypes);
  const handoffGoalClosed =
    Boolean(handedGoalId) && !deepLinkGoal && isStudyTypeClosed(closedStudyTypes, handedGoalId);

  // Seeded into the reducer's initial state rather than dispatched from an
  // effect, for two reasons. It lands on the FIRST render, so a `?goal=` with
  // no handed-off brief cannot flash the goal screen for a frame before replacing
  // it. And because nothing re-applies it, `CLEAR_GOAL` still works: "back to
  // goals" clears the goal for good instead of being undone on the next pass,
  // which an effect keyed on a URL param that never changes would have done.
  // A resumed draft is excluded: the resume hook restores its saved goal.
  const [state, dispatch] = useReducer(
    creationReducer,
    initialCreationState,
    (initial) => {
      if (resumeDraftId) {
        return initial;
      }
      if (deepLinkGoal && !isDeepLinkGoalClosed) {
        return { ...initial, goalId: deepLinkGoal.id };
      }
      if (handoffGoalId) {
        return { ...initial, goalId: handoffGoalId };
      }
      return initial;
    },
  );

  usePublishStudyEmbedDraft(state.questionnaireDraftId);

  const closedDeepLinkToastRef = useRef(false);
  useEffect(() => {
    if ((!isDeepLinkGoalClosed && !handoffGoalClosed) || closedDeepLinkToastRef.current) {
      return;
    }
    closedDeepLinkToastRef.current = true;
    toast.error(t("creation.unavailableType"));
  }, [isDeepLinkGoalClosed, handoffGoalClosed, t]);

  // The picker's audience, until the builder writes it to the draft.
  const selectedPopulation = state.populations.find(
    (p) => p.id === state.selectedPopulationId,
  );
  // Review's audience is the reviewed draft's: its population, price and
  // filtered pool (C09-3, L7a part 4).
  const reviewedPool = reviewedPoolOf(state);

  useStudyCreationData(state, dispatch);
  useReviewedFilteredCount(state.reviewedAudience, dispatch);
  const { isResumingDraft } = useStudyCreationDraftResume(state, dispatch);

  // Show the "what would you like to do?" modal once the duplicated draft finishes loading.
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const duplicateModalShownRef = useRef(false);
  useEffect(() => {
    if (
      !fromDuplicate ||
      isResumingDraft ||
      !state.authReady ||
      duplicateModalShownRef.current
    ) return;
    duplicateModalShownRef.current = true;
    setShowDuplicateModal(true);
  }, [fromDuplicate, isResumingDraft, state.authReady]);

  const [duplicateAudienceModalOpen, setDuplicateAudienceModalOpen] = useState(false);

  const nav = useCreationStepNavigation(state, dispatch);
  const actions = useStudyActions(state, dispatch);
  const { enterReview } = actions;

  const handleDuplicateAction = useCallback(
    (action: DuplicateAction) => {
      setShowDuplicateModal(false);
      switch (action) {
        case "audience":
          setDuplicateAudienceModalOpen(true);
          break;
        case "questionnaire":
          dispatch({ type: "FORCE_STEP", payload: 2 });
          break;
        case "launch": {
          if (!state.selectedPopulationId) {
            // Defense-in-depth: the modal row should already be disabled, but
            // guard here too in case this is called programmatically without an audience.
            setDuplicateAudienceModalOpen(true);
            break;
          }
          // Straight to Review without the builder, through the same entry
          // as the builder's Review: the picked audience is written to the
          // copy, and the verdict decides. A blocked copy stays in the
          // builder with the reason.
          const toastId = toast.loading(t("creation.preparingReview"));
          void enterReview().finally(() => toast.dismiss(toastId));
          break;
        }
      }
    },
    [enterReview, state.selectedPopulationId, t],
  );
  // The leave guards (the browser's "Leave site?" and the sidebar's) live in
  // the questionnaire builder, which owns its unsaved edits and a staged
  // intake. Review holds one more: a typed rename, saved on blur or Enter (a
  // reload in the field never blurs it). While it would be lost, the browser
  // asks first (L13-03). The draft is otherwise persisted, Review's launch
  // options included (`changeReviewOptions`).
  useStudyCreationBeforeUnload(actions.renameUnsaved);

  const { limitReached: trialStudyLimitReached } = useTrialStudyLimit();

  useEffect(() => {
    if (state.currentStep !== 2 || !state.initialMessage) {
      return;
    }
    dispatch({ type: "SET_INITIAL_MESSAGE", payload: null });
  }, [state.currentStep, state.initialMessage, dispatch]);

  const handleStudyDesign = useCallback(
    (design: StudyDesign | null) => {
      dispatch({ type: "SET_SPEC_DESIGN", payload: design });
    },
    [dispatch],
  );

  // The Home brief, handed over as router state (`studyBriefHandoff.ts`, the
  // one reader). It seeds the builder's first turn once, on the reducer's
  // step-1 default, and keeps the goal screen out of the way below.
  const handoffBrief = useStudyBriefHandoff();
  const handoffBriefAppliedRef = useRef(false);
  useEffect(() => {
    if (handoffBriefAppliedRef.current || !handoffBrief) return;
    if (state.currentStep !== 1 || !state.selectedStudyType) return;
    handoffBriefAppliedRef.current = true;
    dispatch({ type: "SET_INITIAL_MESSAGE", payload: handoffBrief });
  }, [handoffBrief, state.currentStep, state.selectedStudyType, dispatch]);

  // Goal-first opening view. Resumed drafts and Home hand-offs skip it —
  // they already carry their own context. Browser back from `?draftId=` is
  // the same route and drops resumeDraftId while currentStep is still 2;
  // wizard steps below must stay gated on this flag so they cannot stack.
  const showGoalScreen =
    !resumeDraftId && !handoffBrief && state.goalId === null;
  const selectedGoal = getGoalById(state.goalId);

  // Pre-select the audience from ?audience= or ?populationId= as soon as data
  // loads. This covers both the goal-screen setup dashboard (where it shows
  // as selected) and the step-1 audience picker (where the filter modal opens
  // for confirmation). The wizard still starts from the first step.
  const deepLinkPreselectedRef = useRef(false);
  const bookmarkPromptedRef = useRef(false);
  // A relaunching audience is picked only once the user confirms it (#1245).
  const [pendingRelaunchSelection, setPendingRelaunchSelection] = useState<{
    populationId: string;
    applyDeepLinkLanguage: boolean;
  } | null>(null);

  function selectPopulation(
    populationId: string,
    applyDeepLinkLanguage: boolean,
  ) {
    dispatch({ type: "SELECT_POPULATION", payload: populationId });
    // Dispatched AFTER SELECT_POPULATION, in the same turn, so the explicit
    // pick wins. SELECT_POPULATION itself sets
    // `contentLanguage: audienceLanguage || state.contentLanguage`
    // (creationState.ts, RND-2668), which would otherwise silently overwrite
    // a language dispatched on mount the moment the audience resolves. A
    // language that disagrees with the audience is a supported case, not a
    // mistake to correct (D2c): an English questionnaire fielded to a
    // Spanish audience is a real, intentional pick.
    //
    // SET_CONTENT_LANGUAGE marks the pick explicit, so a later
    // SELECT_POPULATION (filter apply / audience change) will not clobber it.
    if (
      applyDeepLinkLanguage &&
      deepLinkLanguage &&
      ROOM_LANGUAGES.some((entry) => entry.code === deepLinkLanguage)
    ) {
      dispatch({ type: "SET_CONTENT_LANGUAGE", payload: deepLinkLanguage });
    }
  }

  useEffect(() => {
    if (!deepLinkPopulationId || !state.audienceDataLoaded || deepLinkPreselectedRef.current) {
      return;
    }
    const population = selectDeepLinkedAudience(
      state.populations,
      deepLinkPopulationId,
    );
    if (!population) return;
    deepLinkPreselectedRef.current = true;
    if (isRelaunchingStudyAudienceStatus(population.status)) {
      setPendingRelaunchSelection({
        populationId: population.id,
        applyDeepLinkLanguage: true,
      });
      return;
    }
    selectPopulation(deepLinkPopulationId, true);
    if (handoff?.populationFilter) {
      dispatch({ type: "SET_POPULATION_FILTER", payload: {
        filter: studyHandoffEditorFilter(handoff.populationFilter),
      } });
    }
  }, [deepLinkPopulationId, deepLinkLanguage, handoff, state.audienceDataLoaded, state.populations, dispatch]);

  const onFilterApply = useCallback(
    (
      populationId: string,
      filter: PopulationFilter,
      filteredCount: number,
      selectedSegments?: Array<SelectedSegment>,
      filterLabels?: Array<AudienceFilterLabel>,
    ) => {
      track(AnalyticsEvents.STUDY_AUDIENCE_SELECTED, {
        population_id: populationId,
        has_filter: true,
        filtered_count: filteredCount,
      });
      dispatch({ type: "SELECT_POPULATION", payload: populationId });
      dispatch({
        type: "SET_POPULATION_FILTER",
        payload: { filter, selectedSegments, filterLabels },
      });
      dispatch({ type: "SET_STEP", payload: 2 });
    },
    [dispatch],
  );
  const onContinueWithout = useCallback(
    (populationId: string) => {
      track(AnalyticsEvents.STUDY_AUDIENCE_SELECTED, {
        population_id: populationId,
        has_filter: false,
      });
      dispatch({ type: "SELECT_POPULATION", payload: populationId });
      dispatch({
        type: "SET_POPULATION_FILTER",
        payload: { filter: null },
      });
      dispatch({ type: "SET_STEP", payload: 2 });
    },
    [dispatch],
  );
  const onGoalComplete = useCallback(
    (goalId: GoalId, intake: GoalIntakePayload) => {
      dispatch({ type: "SET_GOAL", payload: goalId });
      dispatch({ type: "SET_INTAKE", payload: intake });
      // Audience already defined on the setup dashboard — go straight to the
      // questionnaire builder. Without one, fall back to the audience step.
      // The filter was already set (or cleared) when the user picked the
      // audience via the setup dashboard filter modal — don't overwrite it.
      if (state.selectedPopulationId) {
        dispatch({ type: "SET_STEP", payload: 2 });
      }
    },
    [dispatch, state.selectedPopulationId],
  );

  // Setup dashboard preselection: default to the most recently bookmarked
  // audience once populations load; the user can change it via the picker.
  // Skip when a URL deep-link already provided the audience to pre-select.
  useEffect(() => {
    if (!showGoalScreen || !state.audienceDataLoaded) return;
    if (state.selectedPopulationId) return;
    if (deepLinkPopulationId) return;
    if (bookmarkPromptedRef.current) return;
    const bookmarked = state.populations
      .filter(
        (population) =>
          population.isBookmarked &&
          isSelectableStudyAudienceStatus(population.status),
      )
      .sort((a, b) =>
        (b.bookmarkedAt ?? "").localeCompare(a.bookmarkedAt ?? ""),
      );
    if (!bookmarked[0]) return;
    bookmarkPromptedRef.current = true;
    if (isRelaunchingStudyAudienceStatus(bookmarked[0].status)) {
      setPendingRelaunchSelection({
        populationId: bookmarked[0].id,
        applyDeepLinkLanguage: false,
      });
      return;
    }
    dispatch({ type: "SELECT_POPULATION", payload: bookmarked[0].id });
  }, [
    showGoalScreen,
    state.audienceDataLoaded,
    state.selectedPopulationId,
    state.populations,
    deepLinkPopulationId,
    dispatch,
  ]);

  const onSelectPopulationFromSetup = useCallback(
    (
      populationId: string,
      filter: PopulationFilter | null,
      filteredCount: number | null,
      selectedSegments?: Array<SelectedSegment>,
      filterLabels?: Array<AudienceFilterLabel>,
    ) => {
      track(AnalyticsEvents.STUDY_AUDIENCE_SELECTED, {
        population_id: populationId,
        has_filter: filter !== null,
        filtered_count: filteredCount,
        source: "goal_setup_dashboard",
      });
      dispatch({ type: "SELECT_POPULATION", payload: populationId });
      dispatch({
        type: "SET_POPULATION_FILTER",
        payload: { filter, selectedSegments, filterLabels },
      });
    },
    [dispatch],
  );
  const onRetryAudienceLoad = useCallback(
    () => dispatch({ type: "RETRY_AUDIENCE_LOAD" }),
    [dispatch],
  );
  const setNumRespondents = useCallback(
    (v: number | null) =>
      dispatch({ type: "SET_NUM_RESPONDENTS", payload: v }),
    [dispatch],
  );
  const setContentLanguage = useCallback(
    (v: string) => dispatch({ type: "SET_CONTENT_LANGUAGE", payload: v }),
    [dispatch],
  );
  const { changeReviewOptions } = actions;
  const setDeckEnabled = useCallback(
    (v: boolean) => changeReviewOptions({ deckEnabled: v }),
    [changeReviewOptions],
  );
  const setTwinChatEnabled = useCallback(
    (v: boolean) => changeReviewOptions({ twinChatEnabled: v }),
    [changeReviewOptions],
  );
  const setStudyMode = useCallback(
    (v: 'quick' | 'full') => changeReviewOptions({ studyMode: v }),
    [changeReviewOptions],
  );
  const setStudyDesign = useCallback(
    (v: StudyDesign) => dispatch({ type: "SET_STUDY_DESIGN", payload: v }),
    [dispatch],
  );

  const editAudience = useCallback(
    () => dispatch({ type: "SET_STEP", payload: 1 }),
    [dispatch],
  );

  // The builder's session holds this draft, created or resumed. From then on
  // the URL names it, so a reload reopens the draft rather than the goal
  // picker. Replaced, not pushed, so Back leaves the flow as it did before.
  const onBuilderDraft = useCallback(
    (draftId: string) => {
      dispatch({ type: "SET_QUESTIONNAIRE_DRAFT_ID", payload: draftId });
      if (draftId !== resumeDraftId) {
        router.replace(ROUTES.draftBuilder(draftId));
      }
    },
    [dispatch, resumeDraftId, router],
  );

  // --- Draft resume banner ---
  // When a draft is created during the session, persist its ID so the next
  // fresh visit to /tests/new can offer to resume it (shown exactly once).
  useEffect(() => {
    if (!state.questionnaireDraftId) return;
    if (isThreadEmbedLocation()) return;
    // A draft created in this session is the one the user is already in; hide
    // the one-time resume offer so Continue never points at a previous draft.
    setResumeBannerDraftId(null);
    try {
      localStorage.setItem(RESUME_DRAFT_STORAGE_KEY, state.questionnaireDraftId);
    } catch { /* storage not available */ }
  }, [state.questionnaireDraftId]);

  const [resumeBannerDraftId, setResumeBannerDraftId] = useState<string | null>(null);
  useEffect(() => {
    // Only show on a fresh visit (no ?draftId= in URL). The study pane
    // never offers the banner, and it does not read or clear the key.
    if (isThreadEmbedLocation()) return;
    if (resumeDraftId) return;
    try {
      const storedId = localStorage.getItem(RESUME_DRAFT_STORAGE_KEY);
      if (!storedId) return;
      // Clear immediately — banner shows exactly once.
      localStorage.removeItem(RESUME_DRAFT_STORAGE_KEY);
      setResumeBannerDraftId(storedId);
    } catch { /* storage not available */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount

  const dismissResumeBanner = useCallback(() => setResumeBannerDraftId(null), []);
  // The builder's "Save and continue", while it is mounted. The banner leaves
  // the builder's draft, so it asks first, as every other control that
  // leaves unsaved edits does (F26 #11): Keep editing stays on this draft.
  const builderSaveFirstRef = useRef<(() => Promise<boolean>) | null>(null);
  const continueResumeDraft = useCallback(async () => {
    const draftId = resumeBannerDraftId;
    if (!draftId) return;
    const saveFirst = builderSaveFirstRef.current;
    if (saveFirst && !(await saveFirst())) return;
    setResumeBannerDraftId(null);
    router.push(ROUTES.draftBuilder(draftId));
  }, [resumeBannerDraftId, router]);

  // --------------------------------

  const studyCreationOpenedRef = useRef(false);
  useEffect(() => {
    if (!state.authReady || studyCreationOpenedRef.current) return;
    studyCreationOpenedRef.current = true;
    track(AnalyticsEvents.STUDY_CREATION_OPENED, {
      resume_draft: Boolean(resumeDraftId),
    });
  }, [state.authReady, resumeDraftId]);

  useEffect(() => {
    if (!state.authReady) return;
    track(AnalyticsEvents.STUDY_CREATION_STEP_VIEWED, {
      step: state.currentStep,
      study_type_id: state.selectedStudyType?.id,
    });
    // Intentionally omit selectedStudyType?.id from deps so same-step changes do not re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.authReady, state.currentStep]);

  if (!state.authReady || isResumingDraft) {
    return (
      <div className="container mx-auto px-4 py-12 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <CreationFlowErrorBoundary>
      <div
        className={
          isEmbed
            ? "bg-background flex flex-col h-dvh min-h-0 overflow-hidden"
            : "bg-background flex flex-col h-dvh overflow-hidden"
        }
        data-testid="study-creation-root"
      >
        <DisplayFontsLoader />

        <div className={isCompactEmbed ? "mx-auto flex min-h-0 w-full flex-1 flex-col px-2 pb-1 pt-2" : "w-full max-w-screen-2xl mx-auto flex-1 flex flex-col min-h-0 px-4 md:px-8 pb-1 pt-8"}>
          {resumeBannerDraftId && (
            <div className="flex flex-shrink-0 items-center justify-between gap-3 rounded-xl border border-border bg-muted px-4 py-3 mb-4 text-sm">
              <span className="text-foreground">
                {t("creation.draftBanner")}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => void continueResumeDraft()}
                >
                  {t("creation.continueDraft")}
                </Button>
                <button
                  type="button"
                  onClick={dismissResumeBanner}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={t("creation.dismissDraft")}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {showGoalScreen && (
            <GoalSelectionStep
              onComplete={onGoalComplete}
              populations={state.populations}
              isAudienceLoading={state.audienceDataLoading}
              selectedPopulationId={state.selectedPopulationId}
              onSelectPopulation={onSelectPopulationFromSetup}
            />
          )}
          {!showGoalScreen && state.currentStep !== 2 && selectedGoal && (
            <div className="flex items-center gap-2 pb-4">
              <GoalChip goal={selectedGoal} />
            </div>
          )}
          {!showGoalScreen && state.currentStep === 1 && !showDuplicateModal && (
            <AudienceStepWrapper
              loading={state.audienceDataLoading}
              loaded={state.audienceDataLoaded}
              audienceLoadError={state.audienceLoadError}
              populations={state.populations}
              confirmedPopulationId={state.selectedPopulationId}
              onFilterApply={onFilterApply}
              onContinueWithout={onContinueWithout}
              onRetryAudienceLoad={onRetryAudienceLoad}
              // A draft's goal is fixed when it is created, so "Change goal"
              // is offered only before the flow holds a draft.
              onBack={
                state.questionnaireDraftId ? undefined : nav.handleBackToGoals
              }
            />
          )}
          {!showGoalScreen && state.currentStep === 2 && !showDuplicateModal && (
            <div className="flex-1 min-h-0 flex flex-col">
              <ChatStepWrapper
                onPresentation={setDraftPresentation}
                onContentLanguageChange={setContentLanguage}
                studyType={state.selectedStudyType ?? STUDY_CREATION_TYPE}
                selectedPopulation={pickedAudienceOf(state)}
                selectedStorageId={state.selectedStorageId}
                storages={state.storages}
                questionnaireDraftId={state.questionnaireDraftId}
                onQuestionnaireDraftIdChange={onBuilderDraft}
                onSurveyReady={actions.enterReview}
                returnToStep={state.returnToStep}
                onReturnToReview={nav.handleReturnToReview}
                onEditAudience={editAudience}
                initialMessage={state.initialMessage}
                sourceThreadToken={handoff?.sourceThreadToken}
                contentLanguage={state.contentLanguage}
                goalId={state.goalId}
                onStudyDesign={handleStudyDesign}
                saveFirstRef={builderSaveFirstRef}
              />
            </div>
          )}
          {!showGoalScreen &&
            (state.currentStep === 3 || showDuplicateModal) &&
            !state.editedSurveyJson && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {t("creation.surveyNotGenerated")}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      dispatch({
                        type: "SET_STEP",
                        payload: 2,
                      })
                    }
                  >
                    {t("creation.goBack")}
                  </Button>
                </div>
              </div>
            )}
          {!showGoalScreen &&
            (state.currentStep === 3 || showDuplicateModal) &&
            state.editedSurveyJson && (
              <ReviewStepWrapper
                studyType={state.selectedStudyType ?? STUDY_CREATION_TYPE}
                goalId={state.goalId}
                editedSurveyJson={state.editedSurveyJson}
                selectedPopulation={reviewedPool.population}
                filteredCount={reviewedPool.filteredCount}
                reviewedAudience={state.reviewedAudience}
                studyName={state.studyName}
                setStudyName={actions.editStudyName}
                onCommitStudyName={actions.commitStudyName}
                numRespondents={state.numRespondents}
                setNumRespondents={setNumRespondents}
                design={state.design}
                setDesign={setStudyDesign}
                creditInfo={state.creditInfo}
                creditsPerRespondent={
                  reviewedPool.population?.creditsPerRespondent ?? 1
                }
                contentLanguage={state.contentLanguage}
                studyMode={state.studyMode}
                onStudyModeChange={setStudyMode}
                isLaunching={state.loading}
                trialStudyLimitReached={trialStudyLimitReached}
                delivery={{
                  deckEnabled: state.deckEnabled,
                  twinChatEnabled: state.twinChatEnabled,
                  onDeckEnabledChange: setDeckEnabled,
                  onTwinChatEnabledChange: setTwinChatEnabled,
                }}
                onBack={nav.handleEditFromReview}
                onLaunch={async () => {
                  await actions.handleSubmit();
                }}
                questionnaireDraftId={state.questionnaireDraftId}
                reviewedRevision={state.reviewedRevision}
              />
            )}
        </div>
      </div>

      <DuplicateActionModal
        open={showDuplicateModal}
        population={selectedPopulation}
        studyName={state.studyName}
        onChoose={handleDuplicateAction}
        // Dismissing keeps the copy (decision B-c): it stays in the studies
        // list to open later, never archived behind the user's back.
        onClose={() => router.push(ROUTES.tests)}
      />

      <RelaunchingAudienceDialog
        open={pendingRelaunchSelection !== null}
        onCancel={() => setPendingRelaunchSelection(null)}
        onConfirm={() => {
          if (!pendingRelaunchSelection) {
            return;
          }
          selectPopulation(
            pendingRelaunchSelection.populationId,
            pendingRelaunchSelection.applyDeepLinkLanguage,
          );
          setPendingRelaunchSelection(null);
        }}
      />

      <StudySetupModal
        open={duplicateAudienceModalOpen}
        onOpenChange={setDuplicateAudienceModalOpen}
        goal={null}
        populations={state.populations}
        selectedPopulationId={state.selectedPopulationId}
        onSelectPopulation={onSelectPopulationFromSetup}
        onContinue={() => {
          setDuplicateAudienceModalOpen(false);
          dispatch({ type: "FORCE_STEP", payload: 2 });
        }}
      />
    </CreationFlowErrorBoundary>
  );
}

export default function StudyCreationFlow() {
  const { t } = useTranslation("app");
  const { data: profile } = useProfile();
  const router = useRouter();
  useStudyEmbedBridge();

  // No profile yet — wait (skeleton handled by parent page layout)
  if (!profile) return null;

  // Tier doesn't include createStudy at all → hard gate (locked_free etc.)
  if (!profile.featureFlags?.createStudy) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[60vh]">
        <LockedFreeGate action={t("studies.createAction")} />
      </div>
    );
  }

  const balance = profile.creditInfo?.balance ?? 0;
  const allowOverage = profile.creditInfo?.allowOverage ?? false;
  const trialExpiredByDate = profile.trialEndsAt
    ? new Date(profile.trialEndsAt) < new Date()
    : false;
  const isTrial = isTrialLike(profile.subscriptionTier);

  // Trial-like user out of credits or expired → upgrade modal
  if (isTrial && (balance <= 0 || trialExpiredByDate)) {
    return <UpgradeModal open onClose={() => router.back()} />;
  }

  // Paid user out of credits and no overage enabled → out-of-credits gate (not upgrade modal)
  if (!isTrial && balance <= 0 && !allowOverage) {
    return (
      <div className="flex flex-1 items-center justify-center min-h-[60vh]">
        <LockedFreeGate
          action={t("creation.runStudiesAction")}
          title={t("creation.outOfCreditsTitle")}
          description={t("creation.outOfCreditsBody")}
          ctaLabel={t("creation.goToBilling")}
          href={ROUTES.settingsTab("billing")}
        />
      </div>
    );
  }

  return <StudyCreationFlowInner />;
}
